// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { IntegrationStatus } from '@/components/integration-status';
import { Link } from '@/components/link';
import {
  KeyboardArrowDownRounded,
  KeyboardArrowRightRounded,
  VisibilityOffOutlined,
} from '@/constants/hardfin-standard-icons';
import { Location } from '@/location/api/types';
import { SiteAddressLabel } from '@/location/components/site-address-label';
import { pluralize } from '@/utilities/pluralize';
import {
  Box,
  Chip,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

interface Props {
  /**
   * A site to display.
   */
  site: Location;

  /**
   * Whether any integration is enabled for the organization
   */
  hasIntegration: boolean;

  /**
   * Whether any site in the current table has visible zones. When false, the
   * leading expander column is omitted from the entire table.
   */
  hasAnyZones: boolean;

  /**
   * Whether this row's zone sub-rows are expanded
   */
  isExpanded?: boolean;

  /**
   * Callback to toggle expansion
   */
  onToggleExpand?: () => void;
}

/**
 * A table row component that renders the name, address, and asset count for a
 * site, with an expandable section showing child zones.
 */
export function SiteTableRow(props: Props) {
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const textColor = props.site.isArchived ? 'text.disabled' : 'text.primary';
  const addressColor = props.site.isArchived
    ? 'text.disabled'
    : props.site.address?.formattedAddress
      ? 'text.primary'
      : 'text.secondary';

  const visibleZones = props.site.zones.filter(
    (z) => !z.isArchived || z.assetCount > 0
  );
  const assetCount = pluralize(props.site.assetCount, { singular: 'asset' });
  const defaultZoneAssetCount =
    props.site.assetCount -
    props.site.zones.reduce((sum, z) => sum + z.assetCount, 0);
  const company = props.site.customer ?? user.organization;
  const hasZones = visibleZones.length > 0;

  return (
    <>
      <TableRow>
        {props.hasAnyZones && (
          <TableCell
            sx={{
              width: '3rem',
              px: 1,
            }}
          >
            {hasZones && (
              <IconButton
                size="small"
                onClick={props.onToggleExpand}
                aria-label={
                  props.isExpanded ? 'Collapse zones' : 'Expand zones'
                }
              >
                {props.isExpanded ? (
                  <KeyboardArrowDownRounded fontSize="small" />
                ) : (
                  <KeyboardArrowRightRounded fontSize="small" />
                )}
              </IconButton>
            )}
          </TableCell>
        )}
        {props.hasIntegration && (
          <TableCell
            width="auto"
            sx={{
              width: '3rem',
              pl: 2,
              pr: 0,
              whiteSpace: 'nowrap',
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="center">
              <IntegrationStatus
                status={props.site.integrations}
                entityType="location"
                entityKey={props.site.key}
                entityDisplayName={props.site.name}
                onResolved={() => {
                  // Listing pages key on ['location', filter] — same
                  // prefix as the detail query. A prefix invalidate
                  // covers every list filter currently mounted.
                  void queryClient.invalidateQueries({
                    queryKey: ['location'],
                  });
                }}
              />
            </Box>
          </TableCell>
        )}
        <TableCell>
          <Stack spacing={1} direction="row" alignItems="center">
            <Link
              to="/locations/$locationKey"
              params={{ locationKey: props.site.key }}
              variant="subtitle2"
              color="primary.main"
            >
              {props.site.name}
            </Link>
            {props.site.isInventory && <Chip size="small" label="Inventory" />}
            {props.site.isTransient && (
              <Chip size="small" label="Transient" variant="outlined" />
            )}
            {props.site.isArchived && (
              <Tooltip title="This location is archived" arrow>
                <VisibilityOffOutlined fontSize="small" color="disabled" />
              </Tooltip>
            )}
          </Stack>
        </TableCell>
        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
          {props.site.isTransient ? (
            <Typography variant="body2" color="text.secondary">
              None
            </Typography>
          ) : (
            <Typography variant="body2" color={textColor}>
              {company.name}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <SiteAddressLabel
            variant="body2"
            color={addressColor}
            site={props.site}
          />
        </TableCell>
        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
          <Link
            to="/assets"
            variant="body2"
            color={textColor}
            search={{ locations: [props.site.id] }}
          >
            {assetCount}
          </Link>
        </TableCell>
        <TableCell align="right" />
      </TableRow>
      {props.isExpanded && (
        <>
          <TableRow sx={{ backgroundColor: 'grey.50' }}>
            {props.hasAnyZones && <TableCell sx={{ py: 0.5 }} />}
            {props.hasIntegration && <TableCell sx={{ py: 0.5 }} />}
            <TableCell sx={{ py: 0.5 }}>
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
                sx={{ pl: 3 }}
              >
                Zone
              </Typography>
            </TableCell>
            <TableCell
              sx={{ py: 0.5, display: { xs: 'none', sm: 'table-cell' } }}
            >
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
              >
                Consignee
              </Typography>
            </TableCell>
            <TableCell sx={{ py: 0.5 }}>
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
              >
                Description
              </Typography>
            </TableCell>
            <TableCell
              sx={{ py: 0.5, display: { xs: 'none', sm: 'table-cell' } }}
            >
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
              >
                Assets
              </Typography>
            </TableCell>
            <TableCell sx={{ py: 0.5 }} />
          </TableRow>
          <TableRow
            hover
            sx={{ cursor: 'pointer' }}
            onClick={() =>
              navigate({
                to: '/locations/$locationKey',
                params: { locationKey: props.site.key },
              })
            }
          >
            {props.hasAnyZones && <TableCell />}
            {props.hasIntegration && <TableCell />}
            <TableCell>
              <Typography variant="body2" sx={{ pl: 3 }}>
                Default zone
              </Typography>
            </TableCell>
            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
              <Typography variant="body2" color="text.secondary">
                {'\u2014'}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2" color="text.secondary">
                {'\u2014'}
              </Typography>
            </TableCell>
            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
              <Typography variant="body2" color="text.secondary">
                {pluralize(defaultZoneAssetCount, { singular: 'asset' })}
              </Typography>
            </TableCell>
            <TableCell />
          </TableRow>
          {props.site.zones
            .filter((z) => !z.isArchived || z.assetCount > 0)
            .map((zone) => (
              <TableRow
                key={zone.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() =>
                  navigate({
                    to: '/locations/$locationKey',
                    params: { locationKey: props.site.key },
                  })
                }
              >
                {props.hasAnyZones && <TableCell />}
                {props.hasIntegration && <TableCell />}
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography
                      variant="body2"
                      sx={{ pl: 3 }}
                      color={
                        zone.isArchived ? 'text.secondary' : 'text.primary'
                      }
                      fontStyle={zone.isArchived ? 'italic' : 'normal'}
                    >
                      {zone.name}
                    </Typography>
                    {zone.isArchived && (
                      <Typography variant="caption" color="text.secondary">
                        (Archived)
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <Typography variant="body2" color="text.secondary">
                    {zone.consignee?.name ?? '\u2014'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {zone.description ?? '\u2014'}
                  </Typography>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <Typography variant="body2" color="text.secondary">
                    {pluralize(zone.assetCount, { singular: 'asset' })}
                  </Typography>
                </TableCell>
                <TableCell />
              </TableRow>
            ))}
        </>
      )}
    </>
  );
}
